import { removeItem, updateItem } from "@common/lib/array";
import { Common } from "@common/types";
import { identity, last } from "lodash";
import React from "react";

export type ModalContextEntryData = {
  close: () => void;
  hide: () => void;
  show: () => void;
};
export type ModalContextEntry = {
  id: string;
  data?: ModalContextEntryData;
};
export interface ModalContextProps {
  closeCurrent: () => void;
  patch: (id: string, entry: ModalContextEntryData) => void;
  register: (id: string) => void;
  unregister: (id: string) => void;
}

export const ModalContext = React.createContext<ModalContextProps>({
  patch: identity,
  register: identity,
  unregister: identity,
  closeCurrent: identity
});

export const ModalContextProvider = (props: Common.WithChildren) => {
  const onEntriesChange = () => {
    if (entries.current.length) {
      document.body.dataset.openModalCount = entries.current.length.toString();
    } else {
      delete document.body.dataset.openModalCount;
    }
  };

  const entries = React.useRef<ModalContextEntry[]>([]);
  const value = React.useMemo(
    (): ModalContextProps => ({
      closeCurrent: () => {
        last(entries.current)?.data?.close();
      },
      patch: (id: string, data: ModalContextEntryData) => {
        entries.current = updateItem(entries.current, id, (x) => ({ ...x, data: data }));
      },
      register: (id: string) => {
        last(entries.current)?.data?.hide();
        entries.current.push({ id });
        onEntriesChange();
      },
      unregister: (id: string) => {
        entries.current = removeItem(entries.current, (x) => x.id === id);
        onEntriesChange();
        last(entries.current)?.data?.show();
      }
    }),
    []
  );

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        value.closeCurrent();
      }
    };

    document.body.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.removeEventListener("keydown", onKeyDown);
    };
  }, [value.closeCurrent]);

  return <ModalContext.Provider value={value}>{props.children}</ModalContext.Provider>;
};
